import { useCallback, useRef } from 'react';
import useIntersectionObserver from 'src/common/hooks/useIntersectionObserver';
import { useCalendarContext } from './useCalendar';

/**
 * IntersectionObserver `threshold` values with "high resolution".
 *
 * We generate values from `0` to `1` in 0.01 increments (101 total steps).
 * This makes the observer callback fire more frequently as the month section
 * scrolls into/out of view, which helps keep the dropdown label feeling
 * responsive during scrolling.
 */
const highResThreshold = Array.from({ length: 101 }, (_, i) => i / 100);

/**
 * Sets the month selector's current label based on when months enter the view.
 * The `ref` should be attached to the container that displays the month's content.
 *
 * This uses an `IntersectionObserver` with a fixed top `rootMargin` to account for
 * the mobile header / controls height (and additional height when `instabookEnabled`).
 *
 * @param date - Month identifier used to update the dropdown label.
 * @returns A ref callback object to attach to the month section element to observe.
 */
const useScrollingMonthUpdate = <T extends HTMLElement>(date: string) => {
  const { setDropdownMonth, instabookEnabled } = useCalendarContext();
  const callback = useCallback(() => {
    setDropdownMonth(date);
  }, [setDropdownMonth, date]);
  const ref = useRef<T>(null);

  // The height of the mobile header and calendar controls
  let offset = 110;

  if (instabookEnabled) {
    // Additional offset to account for the activity and instabook tabs
    offset += 50;
  }

  const onChange = useCallback((entry: IntersectionObserverEntry) => {
    if (
      entry.rootBounds
      && entry.rootBounds.top >= entry.boundingClientRect.top
      && entry.rootBounds.top <= entry.boundingClientRect.bottom
    ) {
      callback();
    }
  }, [callback]);

  useIntersectionObserver({
    ref,
    threshold: highResThreshold,
    rootMargin: `-${offset}px 0px 0px 0px`,
    onChange,
  });
  return ref;
};

export default useScrollingMonthUpdate;
